// Fable's agents on the web: avatar + a short, personal briefing where each one lives (Zach in Sports, Zoe in
// Shop, Kavaya in Travel, Yasmin everywhere) and a "Meet your agents" page.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Radio } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { usePageMeta } from "@/lib/site";
import { api } from "@/lib/api";
import { FlagCityTile } from "@/components/site/FlagCityTile";

export const YASMIN = { key: "yasmin", name: "Yasmin", color: "#E0007A", soft: "#FFE4F1", avatar: "/agents/yasmin-photo.jpg?v=2", avatar_fallback: "/agents/yasmin.png" };

export function AgentAvatar({ a, size = 56, ring = true }) {
  const [src, setSrc] = useState(a ? a.avatar : null);
  useEffect(() => { setSrc(a ? a.avatar : null); }, [a]);
  if (!a) return null;
  return (
    <span className="relative inline-block shrink-0" style={{ width: size, height: size }}>
      <img src={src} onError={() => setSrc(a.avatar_fallback)} alt={a.name} className="h-full w-full rounded-full object-cover object-top" style={ring ? { boxShadow: "0 0 0 3px " + a.soft + ", 0 0 0 5px " + a.color } : undefined} />
      <span className="absolute bottom-0 right-0 h-3.5 w-3.5 rounded-full border-2 border-background bg-emerald-500" aria-hidden="true" />
    </span>
  );
}

function Lines({ lines }) {
  return <div className="space-y-1">{(lines || []).map((l, i) => <p key={i} className="text-sm leading-relaxed">{l}</p>)}</div>;
}

export function AgentBrief({ agent }) {
  const [d, setD] = useState(null);
  const [budget, setBudget] = useState("");
  const [q, setQ] = useState("");
  const load = (extra) => api("/agents/" + agent + "/brief" + (extra || "")).then(setD).catch(() => setD(false));
  useEffect(() => { load(); }, [agent]); // eslint-disable-line react-hooks/exhaustive-deps
  if (d === false) return null;
  if (d === null) return <Skeleton className="h-28 rounded-3xl" />;
  const a = d.agent;
  return (
    <section className="overflow-hidden rounded-3xl border border-border p-5" style={{ background: "linear-gradient(135deg, " + a.soft + ", transparent 70%)" }} data-testid={"agent-" + agent}>
      <div className="flex items-start gap-4">
        <Link to={"/agents/" + a.key} title={"About " + a.name} data-testid={"agent-avatar-link-" + a.key}><AgentAvatar a={a} size={60} /></Link>
        <div className="min-w-0 flex-1">
          <p className="text-sm"><b>{a.name}</b> <span className="text-muted-foreground">· {a.role}</span></p>
          <p className="mt-0.5 font-semibold">{d.hello}.</p>
          <div className="mt-1"><Lines lines={d.lines} /></div>
        </div>
      </div>
      {agent === "zach" && (
        <div className="mt-4 flex flex-wrap gap-2">
          {(d.live || []).concat(d.next || []).slice(0, 4).map((f) => <span key={f.id} className="inline-flex items-center gap-1.5 rounded-full bg-background/80 px-3 py-1 text-xs font-medium">{f.state === "in" && <Radio size={11} className="text-[#E0007A]" />}{f.name}</span>)}
          {!(d.teams || []).length && <Link to="/get-the-app"><Button size="sm" className="rounded-full" style={{ background: a.color }}>Follow your teams in the app</Button></Link>}
        </div>
      )}
      {agent === "zach" && (d.news || []).length > 0 && (
        <div className="mt-4 grid gap-2 sm:grid-cols-2" data-testid="zach-news">
          {d.news.slice(0, 4).map((n) => (
            <a key={n.id} href={n.url || "#"} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 rounded-2xl bg-background/80 p-2 hover:bg-background">
              {n.image_url && <img src={n.image_url} alt="" className="h-12 w-12 shrink-0 rounded-xl object-cover" loading="lazy" />}
              <div className="min-w-0">
                {n.breaking && <span className="text-[10px] font-extrabold tracking-wide text-red-600">BREAKING</span>}
                <p className="line-clamp-2 text-xs font-semibold">{n.title}</p>
                <p className="text-[11px] text-muted-foreground">{n.minutes_ago < 60 ? n.minutes_ago + "m ago" : Math.round(n.minutes_ago / 60) + "h ago"}</p>
              </div>
            </a>
          ))}
        </div>
      )}
      {agent === "zoe" && (
        <>
          <form onSubmit={(e) => { e.preventDefault(); load("?" + new URLSearchParams({ ...(budget ? { budget } : {}), ...(q ? { q } : {}) }).toString()); }} className="mt-4 flex flex-wrap gap-2">
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Who's it for, or what? (e.g. mum, candles)" className="min-w-[200px] flex-1 rounded-full border border-border bg-background px-4 py-2 text-sm outline-none" />
            <input value={budget} onChange={(e) => setBudget(e.target.value.replace(/[^0-9.]/g, ""))} inputMode="decimal" placeholder="Budget £" className="w-28 rounded-full border border-border bg-background px-4 py-2 text-sm outline-none" />
            <Button type="submit" className="rounded-full" style={{ background: a.color }}>Find ideas</Button>
          </form>
          {(d.picks || []).length > 0 && <div className="mt-4 flex gap-3 overflow-x-auto pb-1">{d.picks.slice(0, 10).map((p) => (
            <Link key={p.id} to={"/shop/" + p.id} className="w-32 shrink-0"><div className="aspect-square overflow-hidden rounded-2xl bg-muted">{p.cover && <img src={p.cover} alt={p.title} className="h-full w-full object-cover" loading="lazy" />}</div><p className="mt-1 truncate text-xs font-semibold">{p.title}</p><p className="text-xs">£{p.price_gbp}</p></Link>
          ))}</div>}
        </>
      )}
      {agent === "kavaya" && d.next_trip && (
        <Link to="/travel/trips" className="mt-4 block rounded-2xl bg-background/80 p-3 hover:bg-background" data-testid="kavaya-next-trip">
          <p className="text-[11px] font-bold tracking-wide text-amber-700">NEXT TRIP</p>
          <p className="font-bold">{d.next_trip.city}</p>
          <p className="text-xs text-muted-foreground">{d.next_trip.days === 0 ? "Today" : d.next_trip.days === 1 ? "Tomorrow" : "In " + d.next_trip.days + " days"}{d.next_trip.weather ? " · " + (d.next_trip.weather.emoji || "") + " " + d.next_trip.weather.max + "° / " + d.next_trip.weather.min + "°" : ""}</p>
        </Link>
      )}
      {agent === "kavaya" && (
        <><div className="mt-4 flex flex-wrap gap-2">
          {(d.plans || []).slice(0, 2).map((p) => <Link key={p.id} to="/travel/trips" className="rounded-full bg-background/80 px-3 py-1 text-xs font-semibold">{p.title || "Your trip"}</Link>)}
          </div><div className="mt-3 flex gap-3 overflow-x-auto pb-1 [scrollbar-width:none]" data-testid="kavaya-ideas">{(d.ideas || []).map((i) => <Link key={i.city} to={"/travel/plan?to=" + encodeURIComponent(i.city)} title={i.why} className="group block w-[132px] shrink-0"><FlagCityTile c={{ name: i.city, country: i.country, country_code: i.country_code, iata: i.iata, photo: i.photo, flag: i.flag, hue: i.hue }} sub={i.why} /></Link>)}
        </div></>
      )}
    </section>
  );
}

export function MeetAgents() {
  usePageMeta("Your agents", "Yasmin, Zach, Zoe and Kavaya: Fable's assistants for plans, sport, shopping and travel.");
  const [d, setD] = useState(null);
  useEffect(() => { api("/agents", { auth: false }).then(setD).catch(() => setD({ items: [] })); }, []);
  return (
    <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6" data-testid="meet-agents">
      <p className="text-sm font-semibold text-[#E0007A]">Fable agents</p>
      <h1 className="mt-1 font-serif-display text-4xl font-bold">Four helpers, one account.</h1>
      <p className="mt-2 max-w-2xl text-muted-foreground">Each one lives where you need them and knows what you're into. Yasmin plans and books, Zach follows your teams, Zoe finds the right thing, Kavaya gets you away.</p>
      {!d ? <Skeleton className="mt-8 h-72 rounded-3xl" /> : (
        <div className="mt-8 grid gap-5 sm:grid-cols-2">
          {d.items.map((a) => (
            <Link key={a.key} to={"/agents/" + a.key} className="group rounded-3xl border border-border p-6 transition-shadow hover:shadow-lg" style={{ background: "linear-gradient(160deg, " + a.soft + ", transparent 65%)" }}>
              <AgentAvatar a={a} size={88} />
              <p className="mt-4 text-2xl font-bold">{a.name}</p>
              <p className="text-sm font-semibold" style={{ color: a.color }}>{a.role}</p>
              <p className="mt-2 text-sm text-muted-foreground">{a.tagline}</p>
              <p className="mt-3 rounded-2xl bg-background/80 p-3 text-sm italic">"{a.intro}"</p>
              <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold" style={{ color: a.color }}>About {a.name} <ArrowRight size={15} className="transition-transform group-hover:translate-x-0.5" /></span>
            </Link>
          ))}
        </div>
      )}
      {d && d.credit && <p className="mt-6 text-center text-xs text-muted-foreground">{d.credit}</p>}
    </div>
  );
}
